/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { EndpointUrlParams } from 'src/core/utils/endpointBuilder/types';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { TestBladeActionBar } from 'src/luis/projects/testPane/components/common/TestBladeActionBar';
import { getTestPaneTopAction } from 'src/luis/projects/testPane/components/common/TestPaneTopAction';
import {
    EndpointDebugSettingsModal,
    EndpointDebugSettingsModalDto
} from 'src/luis/projects/testPane/components/EndpointBlade/EndpointDebugSettingsModal';
import { UtteranceInspector } from 'src/luis/projects/testPane/components/UtteranceInspector/UtteranceInspector';
import { EndpointPredictionSwagger } from 'src/luis/projects/testPane/types/swagger/EndpointPredictionSwagger';
import { UtteranceResultStore } from 'src/luis/projects/testPane/types/types';
import { getTopScoringIntent } from 'src/luis/projects/testPane/utils/getTopScoringIntent';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';

type EndpointDebugBladeProps = {
    /**
     * The selected utterance
     */
    selectedUtteranceId: React.ReactText;

    /**
     * The selected published slot to be hit.
     */
    publishSlotToUse: PublishSlotType;

    /**
     * A callback reflects the change in the selected
     * published slot to be hit.
     */
    onPublishSlotToUseChange: (slot: PublishSlotType) => void;

    /**
     * Header to be announced by screen reader
     */
    bladeHeader: string;
};

const messages = defineMessages({
    additionalSettings: {
        id: 'testPane.endpointDebugBlade.additionalSettings',
        defaultMessage: 'Additional Settings'
    },
    showFormattedView: {
        id: 'testPane.endpointDebugBlade.showFormattedView',
        defaultMessage: 'Show formatted view'
    },
    showJSONView: {
        id: 'testPane.endpointDebugBlade.showJSONView',
        defaultMessage: 'Show JSON view'
    },
    publishFirst: {
        id: 'testPane.endpointDebugBlade.publishFirst',
        defaultMessage: 'Please publish your model first.'
    },
    loading: {
        id: 'testPane.endpointDebugBlade.loading',
        defaultMessage: 'Loading...'
    }
});

const AdditionalSettingsActionButton = getTestPaneTopAction('left');
const ToggleFormatActionButton = getTestPaneTopAction('right');

const { getClassName } = getStylistV2('EndpointBlade');
const codeClassName = getClassName('codeClassName', theme => ({ fontFamily: `${theme.fontFamily} !important` }));

/**
 * Renders the publish debug blade in the test pane.
 */
export const EndpointDebugBlade = observer((props: EndpointDebugBladeProps) => {
    const { selectedUtteranceId, publishSlotToUse, onPublishSlotToUseChange, bladeHeader } = props;
    const { localize } = useLocalization();
    const { appId } = useCurrentVersionedAppId();
    const { intents, isLoaded: areIntentsLoaded } = useIntents();
    const { rootStore, dispatcher } = useGlobalState();
    const [isParametersModalHidden, setIsParametersModalHidden] = React.useState(true);
    const [jsonViewOn, setJsonViewOn] = React.useState(false);
    const [utteranceResult, setUtteranceResult] = React.useState<UtteranceResultStore>(null);
    const [jsonResult, setJsonResult] = React.useState(null);

    const predictionStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.testPanePredictionStoreHouse;
    const selectedUtteranceText = predictionStoreHouse.findById(selectedUtteranceId).mainUtterance.utterance.text;

    const { csAccountStore } = rootStore.azureStore.session;
    const [params, setParams] = useUpdateState<EndpointUrlParams>({
        appId,
        log: true,
        verbose: true,
        allIntents: true,
        slot: publishSlotToUse,
        query: selectedUtteranceText,
        // @todo Find a way to get endpoint for staging environments.
        base: rootStore.azureStore.session.csAccountStore.endpoint,
        subscriptionKey: csAccountStore.serviceKey
    });

    const tracker = useProgressTracker({
        onComplete: (results: [UtteranceResultStore, EndpointPredictionSwagger]) => {
            if (results) {
                setUtteranceResult(results[0]);
                setJsonResult(results[1]);
            }
        }
    });

    const isPublished = Boolean(rootStore.currentLuisAppStore.publishSlots[params.slot]);

    const listKeysTracker = useProgressTracker({
        onComplete: () => {
            setParams({ subscriptionKey: csAccountStore.keyStrings.key1 });
        }
    });

    React.useEffect(() => {
        dispatcher.dispatch('fetchAndSetCsAccountKeyByPath', { csAccountStore, path: csAccountStore.path, tracker: listKeysTracker });
    }, [csAccountStore]);

    React.useEffect(() => {
        if (isPublished) {
            dispatcher.dispatch('predictEndpointTestUtterance', {
                params: { ...params, slot: publishSlotToUse, query: selectedUtteranceText },
                tracker
            });
        }
    }, [isPublished, selectedUtteranceText, params]);

    /**
     * Updates the current test pane settings with
     * the modal choices.
     *
     * @param dto The data returned from the modal.
     */
    const onSubmit = (dto: EndpointDebugSettingsModalDto) => {
        setParams(dto);
        onPublishSlotToUseChange(dto.slot);
        setIsParametersModalHidden(true);
    };

    /**
     * Renders the main content of the debug blade.
     */
    const renderContent = () => {
        const loadingSpinner = (
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <Spinner size={SpinnerSize.small} />
                <LuisText>{localize(messages.loading)}</LuisText>
            </Stack>
        );

        if (!isPublished) {
            return <LuisText>{localize(messages.publishFirst)}</LuisText>;
        }

        if (!areIntentsLoaded || tracker.isRunning) {
            return loadingSpinner;
        }

        if (tracker.error) {
            return (
                <MessageBar isMultiline messageBarType={MessageBarType.error}>
                    {tracker.error.message}
                </MessageBar>
            );
        }

        if (utteranceResult === null || jsonResult === null) {
            return loadingSpinner;
        }

        if (jsonViewOn) {
            return (
                <code>
                    <pre className={codeClassName}>{JSON.stringify(jsonResult, null, 2)}</pre>
                </code>
            );
        }

        const topScoringIntent = getTopScoringIntent(utteranceResult.mainUtterance.utterance.predictedIntents);

        return (
            <UtteranceInspector
                isReadOnly
                allIntents={intents}
                selectedIntent={topScoringIntent}
                predictedEntities={utteranceResult.mainUtterance.entities}
                utteranceText={utteranceResult.mainUtterance.utterance.text}
                sentiment={utteranceResult.mainUtterance.utterance.sentiment}
                predictedNDepthEntities={utteranceResult.mainUtterance.nDepths}
                predictedCompositeEntities={utteranceResult.mainUtterance.composites}
                predictedIntents={utteranceResult.mainUtterance.utterance.predictedIntents}
                predictedPatterns={utteranceResult.mainUtterance.utterance.predictedPatterns}
            />
        );
    };

    return (
        <div>
            <Announced message={bladeHeader} />

            <TestBladeActionBar>
                <Stack horizontal horizontalAlign="space-between">
                    <AdditionalSettingsActionButton onClick={() => setIsParametersModalHidden(false)}>
                        {localize(messages.additionalSettings)}
                    </AdditionalSettingsActionButton>

                    <ToggleFormatActionButton onClick={() => setJsonViewOn(!jsonViewOn)}>
                        {localize(jsonViewOn ? messages.showFormattedView : messages.showJSONView)}
                    </ToggleFormatActionButton>
                </Stack>
            </TestBladeActionBar>

            <EndpointDebugSettingsModal
                onSubmit={onSubmit}
                selectedSlot={publishSlotToUse}
                hidden={isParametersModalHidden}
                onDismiss={() => setIsParametersModalHidden(true)}
                selectedCsAccountKeyString={params.subscriptionKey}
            />
            {renderContent()}
        </div>
    );
});
